import { FunctionsHttpError } from '@supabase/supabase-js';
import { getRegistryConfig } from '@/services/supabase/config';
import { createSupabaseClient } from './utils/clientFactory';

const registryClient = createSupabaseClient(getRegistryConfig());

function getFunctionErrorMessage(data: unknown, error: Error): string {
  const payload = data as { error?: string } | null;
  return typeof payload?.error === 'string' ? payload.error : error.message;
}

function isOrganizationNotFound(error: Error, data: unknown): boolean {
  if (error instanceof FunctionsHttpError && error.context.status === 404) {
    return true;
  }
  const payload = data as { error?: string } | null;
  return payload?.error === 'organization_not_found';
}

export async function invokeRegistryFunction<T>(
  functionName: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await registryClient.functions.invoke(functionName, { body });

  if (error) {
    throw new Error(getFunctionErrorMessage(data, error));
  }

  return data as T;
}

const ORGANIZATION_SUBSCRIPTION_FUNCTION = 'organization-subscription';

export async function postOrganizationSubscriptionFunction<T>(
  body: Record<string, unknown>,
): Promise<T> {
  return invokeRegistryFunction<T>(ORGANIZATION_SUBSCRIPTION_FUNCTION, body);
}

const ORGANIZATION_STATUS_FUNCTION = 'organization-status';

export async function postOrganizationStatusFunction<T>(
  body: Record<string, unknown>,
): Promise<T> {
  return invokeRegistryFunction<T>(ORGANIZATION_STATUS_FUNCTION, body);
}

const RESOLVE_ORGANIZATION_FUNCTION = 'resolve-organization';

export async function postResolveOrganizationFunction<T>(
  body: Record<string, unknown>,
): Promise<T | null> {
  const { data, error } = await registryClient.functions.invoke(
    RESOLVE_ORGANIZATION_FUNCTION,
    { body },
  );

  if (error) {
    if (isOrganizationNotFound(error, data)) {
      return null;
    }
    throw new Error(getFunctionErrorMessage(data, error));
  }

  return data as T;
}
